/**
 * ArtifactBlock — рендерит код-блоки ```html и ```svg как preview-tabs
 * (вместо просто текста). Аналог Canvas/Artifacts из ChatGPT/Claude.
 *
 * Пользователь может переключаться: Preview ↔ Source.
 * HTML рендерится в sandboxed iframe (без allow-same-origin),
 * SVG — напрямую через dangerouslySetInnerHTML с базовой санитайзацией.
 *
 * Mermaid — отложено (heavy lib +600KB), будет в следующей итерации.
 */

import { useMemo, useState } from "react";
import { Eye, Code2, Maximize2, ExternalLink } from "lucide-react";
import { cn } from "../../lib/cn";

interface Props {
  language: string;
  code: string;
}

export function ArtifactBlock({ language, code }: Props) {
  const lang = (language || "").toLowerCase();
  const supported = lang === "html" || lang === "svg";

  const [view, setView] = useState<"preview" | "source">(supported ? "preview" : "source");
  const [maximized, setMaximized] = useState(false);

  const previewSrc = useMemo(() => {
    if (lang === "html") {
      // Полная HTML-страница или фрагмент.
      const isFullDoc = /<!doctype html|<html[\s>]/i.test(code);
      return isFullDoc
        ? code
        : `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:Inter,system-ui,sans-serif;padding:1rem;color:#e6e6e6;background:#0d1117;}</style></head><body>${code}</body></html>`;
    }
    if (lang === "svg") {
      const trimmed = code.trim();
      const isWrapped = trimmed.startsWith("<svg");
      return isWrapped
        ? `<!doctype html><html><body style="margin:0;background:#0d1117;display:grid;place-items:center;min-height:100vh;">${code}</body></html>`
        : "";
    }
    return "";
  }, [lang, code]);

  return (
    <div
      className={cn(
        "my-2 rounded-card border border-border/60 overflow-hidden bg-bg-subtle",
        maximized && "fixed inset-4 z-30 my-0",
      )}
    >
      {/* Header — tabs + actions */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border/60 bg-bg/50">
        {supported && (
          <>
            <button
              onClick={() => setView("preview")}
              className={cn(
                "px-2 py-1 rounded text-[0.75rem] font-medium flex items-center gap-1.5 transition-colors",
                view === "preview"
                  ? "bg-accent-500/20 text-accent-300"
                  : "text-fg-muted hover:bg-bg-subtle",
              )}
            >
              <Eye className="size-3" />
              Preview
            </button>
            <button
              onClick={() => setView("source")}
              className={cn(
                "px-2 py-1 rounded text-[0.75rem] font-medium flex items-center gap-1.5 transition-colors",
                view === "source"
                  ? "bg-accent-500/20 text-accent-300"
                  : "text-fg-muted hover:bg-bg-subtle",
              )}
            >
              <Code2 className="size-3" />
              Source
            </button>
          </>
        )}
        <span className="ml-auto text-[0.7rem] uppercase tracking-wide text-fg-subtle">
          {lang || "code"}
        </span>
        {supported && (
          <button
            onClick={() => {
              const w = window.open("", "_blank", "noopener,noreferrer");
              if (w) {
                w.document.write(previewSrc);
                w.document.close();
              }
            }}
            className="p-1 rounded hover:bg-bg-subtle text-fg-muted"
            title="Открыть в новой вкладке"
          >
            <ExternalLink className="size-3" />
          </button>
        )}
        {supported && (
          <button
            onClick={() => setMaximized(!maximized)}
            className="p-1 rounded hover:bg-bg-subtle text-fg-muted"
            title={maximized ? "Свернуть" : "На весь экран"}
          >
            <Maximize2 className="size-3" />
          </button>
        )}
      </div>

      {/* Body */}
      {view === "preview" && supported ? (
        <iframe
          title={`${lang} preview`}
          srcDoc={previewSrc}
          // sandbox без allow-same-origin — iframe не имеет доступа к localStorage родителя.
          sandbox="allow-scripts"
          className={cn(
            "w-full bg-[#0d1117]",
            maximized ? "h-[calc(100vh-7rem)]" : "h-72",
          )}
          referrerPolicy="no-referrer"
        />
      ) : (
        <pre
          className={cn(
            "overflow-auto font-mono text-small p-3 m-0 bg-bg-subtle text-fg",
            maximized ? "h-[calc(100vh-7rem)]" : "max-h-72",
          )}
        >
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}
